import { useEffect, useState } from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { klassenbuchApi } from '../../lib/klassenbuchApi';
import type { AttendanceStatus, KlassenbuchDetail } from '../../lib/klassenbuchApi';
import AttendanceGrid from './AttendanceGrid';
import toast from 'react-hot-toast';

type Props = {
  teachingUnitId: number;
  onClose: () => void;
  onLocked?: () => void;
};

/**
 * Vollbild-Modus "Im Unterricht": minimale Ablenkung, nur Thema-Feld,
 * Anwesenheits-Raster und "Stunde abschließen"-Button.
 */
export default function QuickLessonMode({ teachingUnitId, onClose, onLocked }: Props) {
  const [detail, setDetail] = useState<KlassenbuchDetail | null>(null);
  const [topic, setTopic] = useState('');
  const [previousHomework, setPreviousHomework] = useState<{
    id: number;
    title: string;
    homework_html: string | null;
    homework_due_date: string | null;
    start_at: string | null;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const refresh = async () => {
    const [d, prevHomework] = await Promise.all([
      klassenbuchApi.getEntry(teachingUnitId),
      klassenbuchApi.previousHomework(teachingUnitId).catch(() => null)
    ]);
    setDetail(d);
    setTopic(d.unit.topic_summary || '');
    setPreviousHomework(prevHomework);
  };

  useEffect(() => {
    void refresh();
  }, [teachingUnitId]);

  const readOnly = !!detail?.unit.klassenbuch_locked_at;

  async function saveTopic() {
    if (readOnly) return;
    setSaving(true);
    try {
      await klassenbuchApi.updateEntry(teachingUnitId, { topic_summary: topic || null });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  async function setAttendance(studentKey: string, status: AttendanceStatus) {
    const participant = detail?.participants.find(
      (p) => (p.sophomorixUnid || p.Student_ID || p.username) === studentKey
    );
    const arrivalTime =
      status === 'late' ? new Date().toTimeString().slice(0, 5) : null;
    try {
      await klassenbuchApi.setAttendance(teachingUnitId, studentKey, {
        status,
        arrival_time: arrivalTime
      });
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              participants: prev.participants.map((p) =>
                (p.sophomorixUnid || p.Student_ID || p.username) === studentKey
                  ? { ...p, attendance_status: status, arrival_time: arrivalTime }
                  : p
              )
            }
          : prev
      );
      if (participant && status === 'absent') {
        toast('Eltern werden automatisch per E-Mail informiert (Fehlzeit)', {
          icon: '📧'
        });
      }
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Status nicht speicherbar');
    }
  }

  async function handleLock() {
    setLocking(true);
    try {
      await klassenbuchApi.lock(teachingUnitId);
      toast.success('Stunde abgeschlossen');
      onLocked?.();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Abschluss fehlgeschlagen');
    } finally {
      setLocking(false);
    }
  }

  async function markAllPresent() {
    if (!detail || readOnly) return;
    const keys = detail.participants
      .map((p) => p.sophomorixUnid || p.Student_ID || p.username)
      .filter((x): x is string => !!x);
    if (!keys.length) return;
    setBulkSaving(true);
    try {
      await klassenbuchApi.setAttendanceBulk(teachingUnitId, {
        status: 'present',
        student_keys: keys,
        change_reason: 'quick_all_present'
      });
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              participants: prev.participants.map((p) => ({ ...p, attendance_status: 'present', arrival_time: null }))
            }
          : prev
      );
      toast.success('Alle als anwesend markiert');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Bulk-Aktualisierung fehlgeschlagen');
    } finally {
      setBulkSaving(false);
    }
  }

  async function reusePreviousHomework() {
    if (!previousHomework?.homework_html || readOnly) return;
    try {
      await klassenbuchApi.updateEntry(teachingUnitId, {
        homework_html: previousHomework.homework_html
      });
      toast.success('Hausaufgabe aus letzter Stunde übernommen');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Übernahme fehlgeschlagen');
    }
  }

  if (!detail) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95">
        <div className="text-gray-600">Lade Stunde…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <header className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
        <div>
          <div className="text-xs text-gray-500">
            {detail.unit.class_name || detail.unit.course_name || detail.unit.group_name || ''}
            {detail.unit.start_at ? ` · ${new Date(detail.unit.start_at).toLocaleString('de-DE')}` : ''}
          </div>
          <div className="text-lg font-semibold text-gray-900">{detail.unit.title}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
        >
          <X size={18} /> Schließen
        </button>
      </header>
      <main className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {previousHomework?.homework_html ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <div className="font-medium">Hausaufgabe aus der letzten Stunde gefunden</div>
            <div className="text-xs opacity-80">
              {previousHomework.start_at ? new Date(previousHomework.start_at).toLocaleDateString('de-DE') : ''}
              {previousHomework.title ? ` · ${previousHomework.title}` : ''}
            </div>
            <button
              type="button"
              disabled={readOnly}
              onClick={() => void reusePreviousHomework()}
              className="mt-2 inline-flex items-center rounded border border-amber-300 bg-white px-2 py-1 text-xs hover:bg-amber-100 disabled:opacity-50"
            >
              Hausaufgabe übernehmen
            </button>
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700">Thema der Stunde</label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onBlur={() => void saveTopic()}
            disabled={readOnly}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Kurz und prägnant – Details folgen später"
          />
          {saving && <div className="text-xs text-gray-500 mt-1">Speichere…</div>}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-800">Anwesenheit</h2>
            <button
              type="button"
              disabled={readOnly || bulkSaving}
              onClick={() => void markAllPresent()}
              className="inline-flex items-center rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              {bulkSaving ? 'Setze…' : 'Alle anwesend'}
            </button>
          </div>
          <AttendanceGrid
            participants={detail.participants}
            disabled={readOnly}
            onSet={(key, status) => void setAttendance(key, status)}
          />
        </div>
      </main>
      <footer className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
        <div className="text-xs text-gray-600">
          {readOnly
            ? 'Stunde ist abgeschlossen.'
            : 'Abschluss schreibt Lock-Audit und verhindert weitere Änderungen.'}
        </div>
        <button
          type="button"
          disabled={readOnly || locking}
          onClick={() => void handleLock()}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-emerald-600 text-white text-base font-semibold hover:bg-emerald-700 disabled:opacity-60"
        >
          <CheckCircle2 size={18} />
          {locking ? 'Schließe ab…' : 'Stunde abschließen'}
        </button>
      </footer>
    </div>
  );
}
